import { IconWrapper } from '@/components/ui/IconWrapper';
import { CODES, type CodeString } from '@/consts/codes';
import { ROUTES } from '@/consts/routes';
import type { TranslationKey } from '@/i18n/i18n';
import {
  ApartmentOutlined,
  BankOutlined,
  BarChartOutlined,
  CalculatorOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  EditOutlined,
  FireOutlined,
  GlobalOutlined,
  SafetyCertificateOutlined,
  ShoppingCartOutlined,
  StarFilled,
  TeamOutlined,
  ThunderboltOutlined,
  ToolOutlined
} from '@ant-design/icons';

export interface MenuItem {
  text: TranslationKey;
  to?: string;
  icon?: React.ReactNode;
  children?: MenuItem[];
  badge?: boolean | string;
  code?: CodeString;
  /** If true, this item is hidden from navigation but still used for breadcrumbs */
  hidden?: boolean;
}

export const menu: MenuItem[] = [
  {
    text: 'navigation.recent.title',
    icon: <IconWrapper icon={ClockCircleOutlined} />,
    children: []
  },
  {
    text: 'navigation.favorites.title',
    icon: <IconWrapper icon={StarFilled} className="[&_svg]:text-orange-500" />,
    children: []
  },
  {
    text: 'navigation.pms.title',
    icon: <IconWrapper icon={ToolOutlined} />,
    children: [
      {
        text: 'navigation.pms.children.defineComponentLibrary',
        to: ROUTES.pms.defineComponentLibrary,
        code: CODES.pms.defineComponentLibrary,
        children: [
          {
            text: 'navigation.pms.children.components',
            to: ROUTES.pms.components,
            code: CODES.pms.components,
            children: [
              {
                text: 'navigation.pms.children.componentNew',
                to: ROUTES.pms.componentNew,
                code: CODES.pms.componentNew,
                hidden: true
              },
              {
                text: 'navigation.pms.children.componentEdit',
                to: ROUTES.pms.componentEdit,
                code: CODES.pms.componentEdit,
                hidden: true
              }
            ]
          },
          {
            text: 'navigation.pms.children.spares',
            to: ROUTES.pms.spares,
            code: CODES.pms.spares,
            children: [
              {
                text: 'navigation.pms.children.spareNew',
                to: ROUTES.pms.spareNew,
                code: CODES.pms.spareNew,
                hidden: true
              },
              {
                text: 'navigation.pms.children.spareEdit',
                to: ROUTES.pms.spareEdit,
                code: CODES.pms.spareEdit,
                hidden: true
              }
            ]
          },
          {
            text: 'navigation.pms.children.jobs',
            to: ROUTES.pms.jobs,
            code: CODES.pms.jobs,
            children: [
              {
                text: 'navigation.pms.children.jobNew',
                to: ROUTES.pms.jobNew,
                code: CODES.pms.jobNew,
                hidden: true
              },
              {
                text: 'navigation.pms.children.jobEdit',
                to: ROUTES.pms.jobEdit,
                code: CODES.pms.jobEdit,
                hidden: true
              }
            ]
          }
        ]
      },
      {
        text: 'navigation.pms.children.vesselComponentLibrary',
        to: ROUTES.pms.vesselComponentLibrary,
        code: CODES.pms.vesselComponentLibrary,
        children: [
          {
            text: 'navigation.pms.children.vessels',
            to: ROUTES.pms.vessels,
            code: CODES.pms.vessels,
            hidden: true,
            children: [
              {
                text: 'navigation.pms.children.vesselAssign',
                to: ROUTES.pms.vesselAssign,
                code: CODES.pms.vesselAssign,
                hidden: true
              }
            ]
          }
        ]
      },
      {
        text: 'navigation.pms.children.mainLibraries',
        to: ROUTES.pms.mainLibraries,
        code: CODES.pms.mainLibraries
      }
    ]
  },
  {
    text: 'navigation.pur.title',
    icon: <IconWrapper icon={ShoppingCartOutlined} />,
    children: [
      {
        text: 'navigation.pur.children.overview',
        to: ROUTES.pur.overview,
        code: CODES.pur.overview,
        children: [
          {
            text: 'navigation.pur.children.requisitions',
            to: ROUTES.pur.requisitions,
            code: CODES.pur.requisitions
          },
          {
            text: 'navigation.pur.children.rfq',
            to: ROUTES.pur.rfq,
            code: CODES.pur.rfq
          },
          {
            text: 'navigation.pur.children.purchaseOrders',
            to: ROUTES.pur.purchaseOrders,
            code: CODES.pur.purchaseOrders
          }
        ]
      },
      {
        text: 'navigation.pur.children.vendors',
        to: ROUTES.pur.vendors,
        code: CODES.pur.vendors
      }
    ]
  },
  {
    text: 'navigation.admin.title',
    icon: <IconWrapper icon={ApartmentOutlined} />,
    children: [
      {
        text: 'navigation.admin.children.menu',
        to: ROUTES.legacy.admin.menu,
        code: CODES.admin.menu
      },
      {
        text: 'navigation.admin.children.health',
        to: ROUTES.legacy.admin.health,
        code: CODES.admin.health
      },
      {
        text: 'navigation.admin.children.issues',
        to: ROUTES.legacy.admin.issues,
        code: CODES.admin.issues
      },
      {
        text: 'navigation.admin.children.dashboard',
        to: ROUTES.legacy.admin.dashboard,
        code: CODES.admin.dashboard
      },
      {
        text: 'navigation.admin.children.workItems',
        to: ROUTES.legacy.admin.workItems,
        code: CODES.admin.workItems
      }
    ]
  },
  {
    text: 'navigation.notifications.title',
    icon: <IconWrapper icon={ThunderboltOutlined} />,
    children: [
      {
        text: 'navigation.notifications.children.dashboard',
        to: ROUTES.legacy.notifications.dashboard,
        code: CODES.notifications.dashboard
      }
    ]
  },
  {
    text: 'navigation.accounting.title',
    icon: <IconWrapper icon={BankOutlined} />,
    children: [
      {
        text: 'navigation.accounting.children.freightPayments',
        to: ROUTES.legacy.acc.freightPayments,
        code: CODES.acc.freightPayments
      },
      {
        text: 'navigation.accounting.children.freightPaymentsList',
        to: ROUTES.legacy.acc.freightPaymentsList,
        code: CODES.acc.freightPaymentsList
      },
      {
        text: 'navigation.accounting.children.bbase',
        to: ROUTES.legacy.acc.bbase,
        code: CODES.acc.bbase
      }
    ]
  },
  {
    text: 'navigation.workflows.title',
    icon: <IconWrapper icon={ApartmentOutlined} />,
    children: [
      {
        text: 'navigation.workflows.children.adminDashboard',
        to: ROUTES.legacy.workflows.adminDashboard,
        code: CODES.workflows.adminDashboard
      },
      {
        text: 'navigation.workflows.children.tasks',
        to: ROUTES.legacy.workflows.tasks,
        code: CODES.workflows.tasks
      },
      {
        text: 'navigation.workflows.children.invoices',
        to: ROUTES.legacy.workflows.invoices,
        code: CODES.workflows.invoices
      }
      // {
      //   text: 'navigation.workflows.children.userwfsets',
      //   to: ROUTES.legacy.wf.userwfsets,
      //   code: CODES.wf.userwfsets,
      //   badge: 'HIDDEN'
      // },
      // {
      //   text: 'navigation.workflows.children.wfsets',
      //   to: ROUTES.legacy.workflows.wfsets,
      //   code: CODES.workflows.wfsets,
      //   badge: 'HIDDEN'
      // }
    ]
  },
  {
    text: 'navigation.chartering.title',
    icon: <IconWrapper icon={CalculatorOutlined} />,
    children: [
      {
        text: 'navigation.chartering.children.comp',
        to: ROUTES.legacy.voy.comp,
        code: CODES.voy.comp
      },
      {
        text: 'navigation.chartering.children.calcDet',
        to: ROUTES.legacy.voy.calcDet,
        code: CODES.voy.calcDet
      }
      // {
      //   text: 'navigation.chartering.children.optimal',
      //   to: ROUTES.legacy.voy.optimal,
      //   code: CODES.voy.optimal,
      //   badge: 'HIDDEN'
      // }
    ]
  },
  {
    text: 'navigation.voyagePlanning.title',
    icon: <IconWrapper icon={GlobalOutlined} />,
    children: [
      {
        text: 'navigation.voyagePlanning.children.voyages',
        to: ROUTES.legacy.voy.voyages,
        code: CODES.voy.voyages
      },
      {
        text: 'navigation.voyagePlanning.children.tracker',
        to: ROUTES.legacy.voy.tracker,
        code: CODES.voy.tracker
      },
      {
        text: 'navigation.voyagePlanning.children.tceOverview',
        to: ROUTES.legacy.voy.tceOverview,
        code: CODES.voy.tceOverview
      },
      {
        text: 'navigation.voyagePlanning.children.unsyncedClaims',
        to: ROUTES.legacy.voy.unsyncedClaims,
        code: CODES.voy.unsyncedClaims
      },
      {
        text: 'navigation.voyagePlanning.children.accTracker',
        to: ROUTES.legacy.voy.accTracker,
        code: CODES.voy.accTracker
      },
      {
        text: 'navigation.voyagePlanning.children.works',
        to: ROUTES.legacy.voy.works,
        code: CODES.voy.works
        // security: [
        //   {
        //     "Roles": "ADM"
        //   }
        // ]
      }
      // {
      //   text: 'navigation.voyagePlanning.children.trackerTemplate',
      //   to: ROUTES.legacy.voy.trackerTemplate,
      //   code: CODES.voy.trackerTemplate,
      //   badge: 'HIDDEN'
      // },
      // {
      //   text: 'navigation.voyagePlanning.children.blog',
      //   to: ROUTES.legacy.voy.blog,
      //   code: CODES.voy.blog,
      //   badge: 'HIDDEN'
      // },
      // {
      //   text: 'navigation.voyagePlanning.children.bunkerlogs',
      //   to: ROUTES.legacy.voy.bunkerlogs,
      //   code: CODES.voy.bunkerlogs,
      //   badge: 'HIDDEN'
      // },
      // {
      //   text: 'navigation.voyagePlanning.children.wapb',
      //   to: ROUTES.legacy.voy.wapb,
      //   code: CODES.voy.wapb,
      //   badge: 'HIDDEN'
      // },
      // {
      //   text: 'navigation.voyagePlanning.children.portcallagents',
      //   to: ROUTES.legacy.voy.portcallagents,
      //   code: CODES.voy.portcallagents,
      //   badge: 'HIDDEN'
      // }
    ]
  },
  {
    text: 'navigation.operationReports.title',
    icon: <IconWrapper icon={BarChartOutlined} />,
    children: [
      {
        text: 'navigation.operationReports.children.opdash',
        to: ROUTES.legacy.voy.opdash,
        code: CODES.voy.opdash
      },
      {
        text: 'navigation.operationReports.children.consCurves',
        to: ROUTES.legacy.voy.consCurves,
        code: CODES.voy.consCurves
      },
      {
        text: 'navigation.operationReports.children.voyageSearch',
        to: ROUTES.legacy.voy.voyageSearch,
        code: CODES.voy.voyageSearch
      },
      {
        text: 'navigation.operationReports.children.bunkeringsuppliesreport',
        to: ROUTES.legacy.voy.bunkeringsuppliesreport,
        code: CODES.voy.bunkeringsuppliesreport
      },
      {
        text: 'navigation.operationReports.children.itineraryofvesselreport',
        to: ROUTES.legacy.voy.itineraryofvesselreport,
        code: CODES.voy.itineraryofvesselreport
      },
      {
        text: 'navigation.operationReports.children.cargohistoryandanalysisreport',
        to: ROUTES.legacy.voy.cargohistoryandanalysisreport,
        code: CODES.voy.cargohistoryandanalysisreport
      },
      {
        text: 'navigation.operationReports.children.cargoreport',
        to: ROUTES.legacy.voy.cargoreport,
        code: CODES.voy.cargoreport
      },
      {
        text: 'navigation.operationReports.children.perfreport',
        to: ROUTES.legacy.voy.perfreport,
        code: CODES.voy.perfreport
      },
      {
        text: 'navigation.operationReports.children.pcreport',
        to: ROUTES.legacy.voy.pcreport,
        code: CODES.voy.pcreport
      },
      {
        text: 'navigation.operationReports.children.reportNoonDetails',
        to: ROUTES.legacy.voy.reportNoonDetails,
        code: CODES.voy.reportNoonDetails
      },
      {
        text: 'navigation.operationReports.children.reportVoyageDailyPerformance',
        to: ROUTES.legacy.voy.reportVoyageDailyPerformance,
        code: CODES.voy.reportVoyageDailyPerformance
      },
      // {
      //   text: 'navigation.operationReports.children.noon',
      //   to: ROUTES.legacy.voy.noon,
      //   code: CODES.voy.noon,
      //   badge: 'HIDDEN'
      // },
      // {
      //   text: 'navigation.operationReports.children.arrival',
      //   to: ROUTES.legacy.voy.arrival,
      //   code: CODES.voy.arrival,
      //   badge: 'HIDDEN'
      // },
      // {
      //   text: 'navigation.operationReports.children.departure',
      //   to: ROUTES.legacy.voy.departure,
      //   code: CODES.voy.departure,
      //   badge: 'HIDDEN'
      // },
      // {
      //   text: 'navigation.operationReports.children.portnoon',
      //   to: ROUTES.legacy.voy.portnoon,
      //   code: CODES.voy.portnoon,
      //   badge: 'HIDDEN'
      // },
      {
        text: 'navigation.operationReports.children.reportLatestNoonReports',
        to: ROUTES.legacy.voy.reportLatestNoonReports,
        code: CODES.voy.reportLatestNoonReports
      },
      {
        text: 'navigation.operationReports.children.dailyNoonPerformance',
        to: ROUTES.legacy.voy.dailyNoonPerformance,
        code: CODES.voy.dailyNoonPerformance
      },
      {
        text: 'navigation.operationReports.children.dailyNoonPerformancePerday',
        to: ROUTES.legacy.voy.dailyNoonPerformancePerday,
        code: CODES.voy.dailyNoonPerformancePerday
      },
      // {
      //   text: 'navigation.operationReports.children.dpc',
      //   to: ROUTES.legacy.voy.dpc,
      //   code: CODES.voy.dpc,
      //   badge: 'HIDDEN'
      // },
      // {
      //   text: 'navigation.operationReports.children.lpc',
      //   to: ROUTES.legacy.voy.lpc,
      //   code: CODES.voy.lpc,
      //   badge: 'HIDDEN'
      // },
      // {
      //   text: 'navigation.operationReports.children.stoppage',
      //   to: ROUTES.legacy.voy.stoppage,
      //   code: CODES.voy.stoppage,
      //   badge: 'HIDDEN'
      // },
      {
        text: 'navigation.operationReports.children.stoppageListReport',
        to: ROUTES.legacy.voy.stoppageListReport,
        code: CODES.voy.stoppageListReport
      },
      {
        text: 'navigation.operationReports.children.consumptionsPerGradeReport',
        to: ROUTES.legacy.voy.consumptionsPerGradeReport,
        code: CODES.voy.consumptionsPerGradeReport
      },
      {
        text: 'navigation.operationReports.children.consumptionsPerGradeReportDetailed',
        to: ROUTES.legacy.voy.consumptionsPerGradeReportDetailed,
        code: CODES.voy.consumptionsPerGradeReportDetailed
      },
      {
        text: 'navigation.operationReports.children.weeklyLubricantsReport',
        to: ROUTES.legacy.voy.weeklyLubricantsReport,
        code: CODES.voy.weeklyLubricantsReport
      },
      {
        text: 'navigation.operationReports.children.daysAnalysisReport',
        to: ROUTES.legacy.voy.daysAnalysisReport,
        code: CODES.voy.daysAnalysisReport
      },
      {
        text: 'navigation.operationReports.children.cargoStatisticsReport',
        to: ROUTES.legacy.voy.cargoStatisticsReport,
        code: CODES.voy.cargoStatisticsReport
      },
      {
        text: 'navigation.operationReports.children.ciiReportVoyages',
        to: ROUTES.legacy.voy.ciiReportVoyages,
        code: CODES.voy.ciiReportVoyages
      }
    ]
  },
  {
    text: 'navigation.bunkering.title',
    icon: <IconWrapper icon={FireOutlined} />,
    children: [
      {
        text: 'navigation.bunkering.children.bunkering',
        to: ROUTES.legacy.bun.bunkering,
        code: CODES.bun.bunkering
      },
      {
        text: 'navigation.bunkering.children.grades',
        to: ROUTES.legacy.bun.grades,
        code: CODES.bun.grades
      },
      {
        text: 'navigation.bunkering.children.offers',
        to: ROUTES.legacy.bun.offers,
        code: CODES.bun.offers
      },
      // {
      //   text: 'navigation.bunkering.children.grades2',
      //   to: ROUTES.legacy.bun.grades2,
      //   code: CODES.bun.grades2,
      //   badge: 'HIDDEN'
      // },
      // {
      //   text: 'navigation.bunkering.children.orders',
      //   to: ROUTES.legacy.bun.orders,
      //   code: CODES.bun.orders,
      //   badge: 'HIDDEN'
      // },
      // {
      //   text: 'navigation.bunkering.children.deliveries',
      //   to: ROUTES.legacy.bun.deliveries,
      //   code: CODES.bun.deliveries,
      //   badge: 'HIDDEN'
      // },
      // {
      //   text: 'navigation.bunkering.children.invoices',
      //   to: ROUTES.legacy.bun.invoices,
      //   code: CODES.bun.invoices,
      //   badge: 'HIDDEN'
      // },
      {
        text: 'navigation.bunkering.children.coqcategories',
        to: ROUTES.legacy.bun.coqcategories,
        code: CODES.bun.coqcategories
      },
      {
        text: 'navigation.bunkering.children.coqisostandards',
        to: ROUTES.legacy.bun.coqisostandards,
        code: CODES.bun.coqisostandards
      },
      // {
      //   text: 'navigation.bunkering.children.coqreports',
      //   to: ROUTES.legacy.bun.coqreports,
      //   code: CODES.bun.coqreports,
      //   badge: 'HIDDEN'
      // },
      {
        text: 'navigation.bunkering.children.bunkeringportprices',
        to: ROUTES.legacy.bun.bunkeringportprices,
        code: CODES.bun.bunkeringportprices
      },
      {
        text: 'navigation.bunkering.children.proposedportcalls',
        to: ROUTES.legacy.bun.proposedportcalls,
        code: CODES.bun.proposedportcalls
      }
    ]
  },
  {
    text: 'navigation.freight.title',
    icon: <IconWrapper icon={DollarOutlined} />,
    children: [
      {
        text: 'navigation.freight.children.freight',
        to: ROUTES.legacy.fre.freight,
        code: CODES.fre.freight
      },
      {
        text: 'navigation.freight.children.kpiDashboard',
        to: ROUTES.legacy.fre.kpiDashboard,
        code: CODES.fre.kpiDashboard
        // "Security": [
        //   {
        //     "Roles": "ADM;FRE.Users;OPEADM;FIN.Users"
        //   }
        // ]
      },
      {
        text: 'navigation.freight.children.overview',
        to: ROUTES.legacy.fre.overview,
        code: CODES.fre.overview
      },
      // {
      //   text: 'navigation.freight.children.claims',
      //   to: ROUTES.legacy.fre.claims,
      //   code: CODES.fre.claims,
      //   badge: 'HIDDEN'
      // },
      // {
      //   text: 'navigation.freight.children.laytimeCalculator',
      //   to: ROUTES.legacy.fre.laytimeCalculator,
      //   code: CODES.fre.LaytimeCalculator,
      //   badge: 'HIDDEN'
      // },
      {
        text: 'navigation.freight.children.monitoring',
        to: ROUTES.legacy.fre.monitoring,
        code: CODES.fre.monitoring
      }
    ]
  },
  {
    text: 'navigation.pda.title',
    icon: <IconWrapper icon={BankOutlined} />,
    children: [
      {
        text: 'navigation.pda.children.portexpenses',
        to: ROUTES.legacy.pda.portexpenses,
        code: CODES.pda.portexpenses
      },
      {
        text: 'navigation.pda.children.integrationnotifications',
        to: ROUTES.legacy.pda.integrationnotifications,
        code: CODES.pda.integrationnotifications
      },
      {
        text: 'navigation.pda.children.voyagesintegration',
        to: ROUTES.legacy.pda.voyagesintegration,
        code: CODES.pda.voyagesintegration
      },
      {
        text: 'navigation.pda.children.accountingcode',
        to: ROUTES.legacy.pda.accountingcode,
        code: CODES.pda.accountingcode
      },
      {
        text: 'navigation.pda.children.expensegroups',
        to: ROUTES.legacy.pda.expensegroups,
        code: CODES.pda.expensegroups
      },
      {
        text: 'navigation.pda.children.expensetypes',
        to: ROUTES.legacy.pda.expensetypes,
        code: CODES.pda.expensetypes
      }
    ]
  },
  {
    text: 'navigation.operationData.title',
    icon: <IconWrapper icon={EditOutlined} />,
    children: [
      {
        text: 'navigation.operationData.children.settings',
        to: ROUTES.legacy.voy.settings,
        code: CODES.voy.settings
        // "Security": [
        //   {
        //     "Roles": "ADM"
        //   }
        // ]
      },
      {
        text: 'navigation.operationData.children.portrestrictions',
        to: ROUTES.legacy.voy.portrestrictions,
        code: CODES.voy.portrestrictions
        // "Security": [
        //   {
        //     "Roles": "ADM;OPEADM"
        //   }
        // ]
      },
      {
        text: 'navigation.operationData.children.cptypes',
        to: ROUTES.legacy.voy.cptypes,
        code: CODES.voy.cptypes
        // "Security": [
        //   {
        //     "Roles": "ADM"
        //   }
        // ]
      },
      {
        text: 'navigation.operationData.children.indexValues',
        to: ROUTES.legacy.voy.indexValues,
        code: CODES.voy.indexValues
        // "Security": [
        //   {
        //     "Roles": "ADM"
        //   }
        // ]
      },
      {
        text: 'navigation.operationData.children.porteventtypes',
        to: ROUTES.legacy.voy.porteventtypes,
        code: CODES.voy.porteventtypes
        // "Security": [
        //   {
        //     "Roles": "ADM"
        //   }
        // ]
      },
      {
        text: 'navigation.operationData.children.bjobs',
        to: ROUTES.legacy.voy.bjobs,
        code: CODES.voy.bjobs
        // "Security": [
        //   {
        //     "Roles": "ADM"
        //   }
        // ]
      },
      {
        text: 'navigation.operationData.children.tasklists',
        to: ROUTES.legacy.voy.tasklists,
        code: CODES.voy.tasklists
        // "Security": [
        //   {
        //     "Roles": "ADM"
        //   }
        // ]
      },
      {
        text: 'navigation.operationData.children.tasklistitems',
        to: ROUTES.legacy.voy.tasklistitems,
        code: CODES.voy.tasklistitems
        // "Security": [
        //   {
        //     "Roles": "ADM"
        //   }
        // ]
      },
      {
        text: 'navigation.operationData.children.tasklistitemcategories',
        to: ROUTES.legacy.voy.tasklistitemcategories,
        code: CODES.voy.tasklistitemcategories
        // "Security": [
        //   {
        //     "Roles": "ADM"
        //   }
        // ]
      }
      // {
      //   text: 'navigation.operationData.children.tasklistsandtasks',
      //   to: ROUTES.legacy.voy.tasklistsandtasks,
      //   code: CODES.voy.tasklistsandtasks,
      //   badge: 'HIDDEN'
      // }
    ]
  },
  {
    text: 'navigation.masterDataSignal.title',
    icon: <IconWrapper icon={EditOutlined} />,
    children: [
      {
        text: 'navigation.masterDataSignal.children.vessels',
        to: ROUTES.legacy.master.vessels,
        code: CODES.master.vessels
        // "Security": [
        //   {
        //     "Roles": "ADM"
        //   }
        // ]
      },
      {
        text: 'navigation.masterDataSignal.children.tanks',
        to: ROUTES.legacy.master.tanks,
        code: CODES.master.tanks
        // "Security": [
        //   {
        //     "Roles": "ADM"
        //   }
        // ]
      },
      {
        text: 'navigation.masterDataSignal.children.vesselspecgroups',
        to: ROUTES.legacy.master.vesselspecgroups,
        code: CODES.master.vesselspecgroups
        // "Security": [
        //   {
        //     "Roles": "ADM"
        //   }
        // ]
      },
      {
        text: 'navigation.masterDataSignal.children.incomecategories',
        to: ROUTES.legacy.md.incomecategories,
        code: CODES.md.incomecategories
        // "Security": [
        //   {
        //     "Roles": "ADM"
        //   }
        // ]
      },
      {
        text: 'navigation.masterDataSignal.children.incomereasons',
        to: ROUTES.legacy.md.incomereasons,
        code: CODES.md.incomereasons
        // "Security": [
        //   {
        //     "Roles": "ADM"
        //   }
        // ]
      },
      {
        text: 'navigation.masterDataSignal.children.vesselclasses',
        to: ROUTES.legacy.master.vesselclasses,
        code: CODES.master.vesselclasses
        // "Security": [
        //   {
        //     "Roles": "ADM"
        //   }
        // ]
      },
      {
        text: 'navigation.masterDataSignal.children.licenseranks',
        to: ROUTES.legacy.master.licenseranks,
        code: CODES.master.licenseranks
        // "Security": [
        //   {
        //     "Roles": "ADM"
        //   }
        // ]
      },
      {
        text: 'navigation.masterDataSignal.children.dutyranks',
        to: ROUTES.legacy.master.dutyranks,
        code: CODES.master.dutyranks
        // "Security": [
        //   {
        //     "Roles": "ADM"
        //   }
        // ]
      },
      {
        text: 'navigation.masterDataSignal.children.payrollranks',
        to: ROUTES.legacy.master.payrollranks,
        code: CODES.master.payrollranks
        // "Security": [
        //   {
        //     "Roles": "ADM"
        //   }
        // ]
      },
      {
        text: 'navigation.masterDataSignal.children.vesseltypes',
        to: ROUTES.legacy.master.vesseltypes,
        code: CODES.master.vesseltypes
        // "Security": [
        //   {
        //     "Roles": "ADM"
        //   }
        // ]
      },
      {
        text: 'navigation.masterDataSignal.children.companies',
        to: ROUTES.legacy.master.companies,
        code: CODES.master.companies
        // "Security": [
        //   {
        //     "Roles": "ADM;COMP.Editors"
        //   }
        // ]
      },
      {
        text: 'navigation.masterDataSignal.children.fueltypes',
        to: ROUTES.legacy.master.fueltypes,
        code: CODES.master.fueltypes
        // "Security": [
        //   {
        //     "Roles": "ADM"
        //   }
        // ]
      },
      {
        text: 'navigation.masterDataSignal.children.expenseCategories',
        to: ROUTES.legacy.md.expenseCategories,
        code: CODES.md.expenseCategories
        // "Security": [
        //   {
        //     "Roles": "ADM"
        //   }
        // ]
      },
      {
        text: 'navigation.masterDataSignal.children.ports',
        to: ROUTES.legacy.master.ports,
        code: CODES.master.ports
        // "Security": [
        //   {
        //     "Roles": "ADM"
        //   }
        // ]
      },
      {
        text: 'navigation.masterDataSignal.children.cargotypes',
        to: ROUTES.legacy.master.cargotypes,
        code: CODES.master.cargotypes
        // "Security": [
        //   {
        //     "Roles": "ADM"
        //   }
        // ]
      },
      {
        text: 'navigation.masterDataSignal.children.cargogrades',
        to: ROUTES.legacy.master.cargogrades,
        code: CODES.master.cargogrades
        // "Security": [
        //   {
        //     "Roles": "ADM"
        //   }
        // ]
      },
      {
        text: 'navigation.masterDataSignal.children.flags',
        to: ROUTES.legacy.master.flags,
        code: CODES.master.flags
        // "Security": [
        //   {
        //     "Roles": "ADM"
        //   }
        // ]
      },
      {
        text: 'navigation.masterDataSignal.children.areas',
        to: ROUTES.legacy.md.areas,
        code: CODES.md.areas
        // "Security": [
        //   {
        //     "Roles": "ADM"
        //   }
        // ]
      },
      {
        text: 'navigation.masterDataSignal.children.countries',
        to: ROUTES.legacy.md.countries,
        code: CODES.md.countries
        // "Security": [
        //   {
        //     "Roles": "ADM"
        //   }
        // ]
      },
      {
        text: 'navigation.masterDataSignal.children.cities',
        to: ROUTES.legacy.md.cities,
        code: CODES.md.cities
        // "Security": [
        //   {
        //     "Roles": "ADM"
        //   }
        // ]
      },
      {
        text: 'navigation.masterDataSignal.children.nationalities',
        to: ROUTES.legacy.md.nationalities,
        code: CODES.md.nationalities
        // "Security": [
        //   {
        //     "Roles": "ADM"
        //   }
        // ]
      },
      {
        text: 'navigation.masterDataSignal.children.persons',
        to: ROUTES.legacy.master.persons,
        code: CODES.master.persons
        // "Security": [
        //   {
        //     "Roles": "ADM"
        //   }
        // ]
      },
      {
        text: 'navigation.masterDataSignal.children.ratings',
        to: ROUTES.legacy.master.ratings,
        code: CODES.master.ratings
        // "Security": [
        //   {
        //     "Roles": "ADM;FIN.Users"
        //   }
        // ]
      },
      {
        text: 'navigation.masterDataSignal.children.companyRatings',
        to: ROUTES.legacy.master.companyRatings,
        code: CODES.master.companyRatings
        // "Security": [
        //   {
        //     "Roles": "ADM;FIN.Users"
        //   }
        // ]
      }
    ]
  },
  {
    text: 'navigation.userManagementSignal.title',
    icon: <IconWrapper icon={TeamOutlined} />,
    children: [
      {
        text: 'navigation.userManagementSignal.children.customUsers',
        to: ROUTES.legacy.master.customUsers,
        code: CODES.master.customUsers
      },
      {
        text: 'navigation.userManagementSignal.children.departments',
        to: ROUTES.legacy.master.departments,
        code: CODES.master.departments
      },
      {
        text: 'navigation.userManagementSignal.children.usercompanies',
        to: ROUTES.legacy.md.usercompanies,
        code: CODES.md.usercompanies
      },
      {
        text: 'navigation.userManagementSignal.children.userdepartments',
        to: ROUTES.legacy.md.userdepartments,
        code: CODES.md.userdepartments
      },
      {
        text: 'navigation.userManagementSignal.children.userpositions',
        to: ROUTES.legacy.md.userpositions,
        code: CODES.md.userpositions
      },
      {
        text: 'navigation.userManagementSignal.children.userRoles',
        to: ROUTES.legacy.md.userRoles,
        code: CODES.md.userRoles
      },
      {
        text: 'navigation.userManagementSignal.children.deppos',
        to: ROUTES.legacy.master.deppos,
        code: CODES.master.deppos
      },
      {
        text: 'navigation.userManagementSignal.children.roles',
        to: ROUTES.legacy.master.roles,
        code: CODES.master.roles
      },
      {
        text: 'navigation.userManagementSignal.children.usersettings',
        to: ROUTES.legacy.master.usersettings,
        code: CODES.master.usersettings
      }
    ]
  },
  {
    text: 'navigation.inspectionsManagement.title',
    icon: <IconWrapper icon={ApartmentOutlined} />,
    children: [
      {
        text: 'navigation.inspectionsManagement.children.drills',
        to: ROUTES.ism.drills,
        code: CODES.ism.drills
      }
    ]
  },
  {
    text: 'navigation.inspectionsManagementLegacy.title',
    icon: <IconWrapper icon={SafetyCertificateOutlined} />,
    children: [
      {
        text: 'navigation.inspectionsManagementLegacy.children.drills',
        to: ROUTES.legacy.ism.drills,
        code: CODES.ism.drills
      },
      {
        text: 'navigation.inspectionsManagementLegacy.children.drillTypes',
        to: ROUTES.legacy.ism.drillTypes,
        code: CODES.ism.drillTypes
      }
    ]
  }
];
